import { isArray } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import BodyHeaderComponent from '@components/BodyHeaderComponent';
import LoaderComponent from '@components/LoaderComponent';

import useDocumentTitle from '@hooks/useDocumentTitle';

import ClientsTabComponent from '@features/client/components/ClientsTabComponent';
import ReviewModal from '@features/client/components/ReviewModal';
import { allFilterPages, clientListFields } from '@features/filter/static/filterUtil';
import FilterComponent from '@features/filter/v2/FilterComponent';

import { clearGlobalFilterWithExcludeField } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { getAccountManagers, getBrandList, getStatus } from '@store/filter/action';
import { Types } from '@store/types';

import ApiClient from '@lib/apiClient';

import { clothingLabelStatus, isPageReachBottom, printApiError } from '@utils/Util';

const ManageClient = () => {
	const [state, setState] = useState({
		projectList: [],
		clientList: [],
		page: 0,
		size: 15,
		name: '',
		email: '',
		companyName: '',
		managerId: '',
		executiveId: '',
		status: '',
		sort: 'id,desc',
		fromDate: '',
		toDate: '',
		startDate: '',
		statusList: [],
		height: window.innerHeight,
		managerList: [],
		executiveList: [],
		totalCount: 0,
		pendingCount: 0,
		show: false,
		selectedItem: {},
		clientInfo: {},
		reviewModal: false
	});
	const { brandList, accountManagerList, projectManagerList, statusList } = useSelector(
		(store) => store.filters
	);
	const filterFields = useSelector((store) => store.filterFieldStore);
	const dispatch = useDispatch();
	const loadingRef = useRef(false);
	const clientsRef = useRef(null);
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState('ALL');
	const [showFilter, setShowFilter] = useState(true);
	const [sortType, setSortType] = useState({ label: 'Newest First', value: 'id,desc' });
	const [search, setSearch] = useState('');
	useEffect(() => {
		Promise.all([dispatch(getBrandList()), dispatch(getAccountManagers()), dispatch(getStatus())]);
	}, []);
	useEffect(() => {
		const filterList = {
			brandList: brandList,
			accountManagerList: accountManagerList,
			projectManagerList: projectManagerList,
			statusList: statusList
		};
		dispatch({
			type: Types.STORE_DEPENDENT_FILTER_LIST,
			payload: { responseData: filterList, filterListPage: 'CLIENT_LIST' }
		});
	}, [brandList, accountManagerList, projectManagerList]);
	useDocumentTitle('Client list - Nitex - The easiest clothing manufacturing software');
	const handleScroll = async () => {
		if (isPageReachBottom() && clientsRef.current !== null && loadingRef.current === false) {
			const { data, totalElements, currentPage } = clientsRef.current;
			if (totalElements > 0 && totalElements !== data.length) {
				await renderList(currentPage + 1, true);
			}
		}
	};
	const buildFilterParams = () => {
		let filterParams = {};
		for (const field in filterFields) {
			if (isArray(filterFields[field])) {
				if (field === 'filterSubCategoryList') {
					filterParams['status'] = filterFields[field].map((item) => item.key).join(',');
				}
				if (field === 'brandIds') {
					filterParams['brandId'] = filterFields[field].map((item) => item.key).join(',');
				}
				if (field === 'accountManagerIdList') {
					filterParams['executiveId'] = filterFields[field].map((item) => item.key).join(',');
				}
			}
		}
		return filterParams;
	};
	const renderList = async (page = 0, merge = true) => {
		loadingRef.current = true;
		let filterOptions = buildFilterParams();
		const params = { page, sort: sortType.value, size: state.size, name: search, ...filterOptions };
		if (activeTab === 'PENDING') {
			params['executiveId'] = JSON.parse(localStorage.getItem('userInfo')).id;
			params['status'] = 'PENDING';
		}
		try {
			dispatch(openLoader());
			const { data } = await ApiClient.GET('getClients', params);
			const dataList = merge ? [...(clientsRef.current?.data || []), ...data.data] : data.data;
			clientsRef.current = { data: dataList, totalElements: data.totalCount, currentPage: page };
			setState({
				...state,
				clientList: dataList,
				page,
				totalCount: data.totalCount,
				pendingCount: activeTab === 'PENDING' ? data.totalCount : 0
			});
			loadingRef.current = false;
			dispatch(closeLoader());
		} catch (error) {
			loadingRef.current = false;
			dispatch(closeLoader());
			printApiError(error);
		}
	};
	useEffect(() => {
		(async () => {
			window.scrollTo(0, 0);
			await renderList(0, false);
		})();
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, [activeTab, filterFields, sortType.value, search]);
	const handleTabChange = async (tabName) => {
		dispatch(clearGlobalFilterWithExcludeField(['']));
		setSearch('');
		setActiveTab(tabName);
	};
	const renderStatusButton = (item) => {
		const { status, reason } = item;
		if (status === 'ACTIVE') {
			return (
				<div className="task-status green">
					{' '}
					<span className="status-btn">Active</span>{' '}
				</div>
			);
		} else if (status === 'PENDING') {
			return (
				<div className="task-status purple">
					{' '}
					<span className="status-btn">Pending</span>{' '}
					{reason === 'WRONG_NUMBER' && <span className="secondary">Right number needed</span>}{' '}
					{reason === 'CALL_NOT_RECEIVED' && <span className="secondary">Contact Later</span>}{' '}
				</div>
			);
		}
		return (
			<div className="task-status red">
				{' '}
				<span className="status-btn">Disabled</span>{' '}
			</div>
		);
	};
	return (
		<LoaderComponent loading={loadingRef.current}>
			{' '}
			<BodyHeaderComponent
				whiteBg={true}
				isFilterEnabled={activeTab === 'ALL'}
				callback={() => false}
				title="Buyer List"
				addNewButtonText={activeTab === 'ALL' ? 'Buyer' : undefined}
				addNewButtonCallback={() => navigate('/clients/add')}
				childElements={
					<ClientsTabComponent
						count={state.pendingCount}
						triggerTab={handleTabChange}
						activeItem={activeTab}
					/>
				}
				totalElements={state.totalCount}
				setShowFilter={setShowFilter}
				sortType={sortType}
				setSortType={setSortType}
				search={search}
				setSearch={setSearch}
			/>{' '}
			<section className="bg-white filter-data-container filter-v3-data-container clients-data-container bg-white py-0">
				{' '}
				{activeTab === 'ALL' && (
					<FilterComponent
						filterPageName={allFilterPages.CLIENT_LIST}
						fields={clientListFields}
						showFilter={showFilter}
						isDependentFilter={true}
					/>
				)}{' '}
				<div className="data-table scroll-x-label">
					{' '}
					<div className="admin-list-table-section clients-list-table-section">
						{' '}
						{state.clientList.length > 0 && (
							<table className="table">
								{' '}
								<thead>
									{' '}
									<tr>
										{' '}
										<th>Buyer name</th> <th>Brand</th> <th>Phone number</th>{' '}
										<th>Work mail</th> <th>Account manager</th> <th>Status</th>{' '}
										<th className="text-right">Actions</th>{' '}
									</tr>{' '}
								</thead>{' '}
								<tbody>
									{' '}
									{state.clientList.map((item) => (
										<tr key={item.id}>
											{' '}
											<td>{item.name}</td>{' '}
											<td>
												{' '}
												{item.brandResponse?.name ||
													item.userInputtedBrandName ||
													'N/A'}{' '}
											</td>{' '}
											<td>{item.phoneNumber}</td> <td>{item.email}</td>{' '}
											<td>{item.accountExecutiveName}</td>{' '}
											<td>{renderStatusButton(item)}</td>{' '}
											<td className="text-right">
												{' '}
												<a onClick={() => navigate('/clients/edit/' + item.id)}>
													{' '}
													Edit{' '}
												</a>{' '}
											</td>{' '}
										</tr>
									))}{' '}
								</tbody>{' '}
							</table>
						)}{' '}
					</div>{' '}
				</div>{' '}
				<Modal
					show={state.show}
					onHide={() => setState((prev) => ({ ...prev, show: false, selectedItem: {} }))}
					dialogClassName="modal-90w"
				>
					{' '}
					<Modal.Header closeButton>
						{' '}
						<Modal.Title className="semibold-16">Client more info</Modal.Title>{' '}
					</Modal.Header>{' '}
					<Modal.Body>
						{' '}
						<div className="client-more-info">
							{' '}
							<ul>
								{' '}
								<li>
									{' '}
									<strong>Client name:</strong>{' '}
									<span>{state.selectedItem.name || 'N/A'}</span>{' '}
								</li>{' '}
								<li>
									{' '}
									<strong>Client email:</strong>{' '}
									<span>{state.selectedItem.email}</span>{' '}
								</li>{' '}
								<li>
									{' '}
									<strong>Role in business:</strong>{' '}
									<span>{state.selectedItem.roleInBusiness || 'N/A'}</span>{' '}
								</li>{' '}
								<li>
									{' '}
									<strong>Label status:</strong>{' '}
									<span>
										{clothingLabelStatus(state.selectedItem.clothingLabelStatus)}
									</span>{' '}
								</li>{' '}
								<li>
									{' '}
									<strong>Client num:</strong>{' '}
									<span>{state.selectedItem.phoneNumber || 'N/A'}</span>{' '}
								</li>{' '}
							</ul>{' '}
						</div>{' '}
					</Modal.Body>{' '}
				</Modal>{' '}
				{state.reviewModal && (
					<ReviewModal
						showModal={state.reviewModal}
						handleClose={() => setState((prev) => ({ ...prev, reviewModal: false }))}
					/>
				)}{' '}
			</section>{' '}
		</LoaderComponent>
	);
};
export default ManageClient;
